import {
  Box,
  Checkbox,
  // Checkbox,
  CheckboxGroup,
  Heading,
  HStack,
  Table,
  TableContainer,
  Tbody,
  Text,
  Tr,
} from '@chakra-ui/react';
import { useState } from 'react';

import {
  CustomTd,
  CustomTh,
  CustomThead,
  CustomTr,
} from '../../../../../components/custom';
import {
  QueueListWithBgIcon,
  UTurnRightWithYellowBgIcon,
} from '../../../../../components/icons';
import {
  formatDateAndTime,
  formatNumber,
  getTitleCase,
} from '../../../../../helpers/utilityFunctions';
import { useAppSelector } from '../../../../../store/hooks';
import { IPendingTransactionForApproval } from '../../../../../types/pendingTransaction';
import AwaitingApprovalTableActions from './AwaitingApprovalTableActions';
import BulkApprovalActions from './BulkApprovalActions';

interface IAwaitingApprovalTable {
  filteredTransactions: IPendingTransactionForApproval[] | undefined;
}

export default function AwaitingApprovalTable({
  filteredTransactions,
}: IAwaitingApprovalTable) {
  const { status } = useAppSelector(
    (state) => state.getTransactionsPendingApproval
  );

  const isLoading = status === 'loading';

  const [selectedTransactions, setSelectedTransactions] = useState<
    (string | number)[]
  >([]);

  const handleSelectAllTransactions = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setSelectedTransactions(
      e.target.checked
        ? filteredTransactions?.map((t) => t?.id ?? '') ?? []
        : []
    );
  };

  const handleResetSelectedTransactions = () => {
    setSelectedTransactions([]);
  };

  return (
    <Box
      border="1px solid #FCE7BB"
      borderRadius="24px"
      padding={6}
      minH="100%"
      bg="#FFF8EB"
    >
      <HStack justifyContent="space-between" alignItems="center">
        <HStack spacing={{ base: '8px', md: '18px' }} mb={6}>
          {selectedTransactions.length > 0 ? (
            <UTurnRightWithYellowBgIcon boxSize="32px" />
          ) : (
            <QueueListWithBgIcon boxSize="32px" />
          )}

          <Heading
            as="h2"
            fontWeight={700}
            fontSize="1.125rem"
            lineHeight="133%"
            noOfLines={1}
          >
            {selectedTransactions.length > 0
              ? `${selectedTransactions.length} Selected`
              : 'Awaiting Approval'}
          </Heading>
        </HStack>

        <HStack spacing={2}>
          {selectedTransactions.length > 0 && (
            <BulkApprovalActions
              selectedTransactions={selectedTransactions}
              handleResetSelectedTransactions={handleResetSelectedTransactions}
            />
          )}
        </HStack>
      </HStack>

      <Box>
        <TableContainer>
          <Table variant="unstyled">
            <CustomThead
              sx={{
                background: '#FCE7BB',
              }}
            >
              <Tr>
                <CustomTh>
                  <Checkbox
                    border="#DEE1E1"
                    boxSize="15.83px"
                    borderRadius="1.5px"
                    colorScheme="richBlack"
                    value="all"
                    isDisabled={
                      status !== 'success' || filteredTransactions?.length === 0
                    }
                    onChange={handleSelectAllTransactions}
                  />
                </CustomTh>
                <CustomTh>Initiator</CustomTh>
                <CustomTh>Amount</CustomTh>
                <CustomTh>Transaction Type</CustomTh>
                <CustomTh>Date</CustomTh>
                <CustomTh borderRadius="0 8px 8px 0">Actions</CustomTh>
              </Tr>
            </CustomThead>
            <Tbody>
              {status === 'loading' ? (
                <>
                  {Array.from({ length: 7 }).map((_, index) => (
                    <CustomTr key={index}>
                      <CustomTd isLoading={isLoading}>...</CustomTd>
                      <CustomTd isLoading={isLoading}>...</CustomTd>
                      <CustomTd isLoading={isLoading}>...</CustomTd>
                      <CustomTd isLoading={isLoading}>...</CustomTd>
                      <CustomTd isLoading={isLoading}>...</CustomTd>
                      <CustomTd isLoading={isLoading}>...</CustomTd>
                    </CustomTr>
                  ))}
                </>
              ) : (
                <CheckboxGroup
                  colorScheme="richBlack"
                  value={selectedTransactions}
                  onChange={(e) => {
                    setSelectedTransactions(e);
                  }}
                >
                  {filteredTransactions?.map((transaction) => {
                    const amountInNaira = transaction?.transactionAmount
                      ? transaction?.transactionAmount / 100
                      : 0;

                    return (
                      <CustomTr
                        key={transaction.id}
                        sx={{
                          background: 'neutral.100',
                          borderY: '3px solid #FCE7BB',
                        }}
                      >
                        <CustomTd
                          textTransform="capitalize"
                          isLoading={isLoading}
                        >
                          <Checkbox
                            border="#DEE1E1"
                            boxSize="15.83px"
                            borderRadius="1.5px"
                            value={transaction?.id}
                          />
                        </CustomTd>
                        <CustomTd
                          densePadding
                          borderRadius="9px 0 0 9px"
                          isLoading={isLoading}
                        >
                          {transaction && (
                            <Text>
                              {getTitleCase(
                                transaction?.initiatedBy?.user?.firstName +
                                  ' ' +
                                  transaction?.initiatedBy?.user?.lastName
                              )}
                            </Text>
                          )}
                        </CustomTd>
                        <CustomTd isLoading={isLoading}>
                          N{' '}
                          {formatNumber(amountInNaira).formattedStringWithKobo}
                        </CustomTd>
                        <CustomTd
                          textTransform="capitalize"
                          isLoading={isLoading}
                        >
                          {getTitleCase(transaction?.transactionType) ?? 'N/A'}
                        </CustomTd>
                        <CustomTd isLoading={isLoading}>
                          {formatDateAndTime(transaction?.initiatedAt ?? '')}
                        </CustomTd>
                        <CustomTd
                          noPadding
                          borderRadius="0 8px 8px 0"
                          isLoading={isLoading}
                        >
                          <AwaitingApprovalTableActions
                            transaction={transaction}
                          />
                        </CustomTd>
                      </CustomTr>
                    );
                  })}
                </CheckboxGroup>
              )}
            </Tbody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
}
